import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  DataTable,
  Page,
  Spinner,
  Banner,
  Layout,
} from "@shopify/polaris";
import { useAdmin } from "context/admin";
import { fetcher } from "util/fetch";

function exportToCsv(rows, headers, filename) {
  const dataWithHeaders = [headers, ...rows];
  const csvContent = dataWithHeaders
    .map((row) =>
      row
        .map((cell) =>
          String(cell).includes(",") ? `"${String(cell).replace(/"/g, '""')}"` : cell
        )
        .join(",")
    )
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function SortableTable({ headings, initialRows, columnContentTypes }) {
  const [sortedRows, setSortedRows] = useState(initialRows);

  useEffect(() => {
    setSortedRows(initialRows);
  }, [initialRows]);

  const handleSort = useCallback(
    (index, direction) => {
      setSortedRows((oldRows) =>
        [...oldRows].sort((rowA, rowB) => {
          const a = rowA[index];
          const b = rowB[index];

          if (typeof a === "string" && typeof b === "string") {
            return direction === "descending"
              ? b.localeCompare(a)
              : a.localeCompare(b);
          } else {
            return direction === "descending" ? b - a : a - b;
          }
        })
      );
    },
    []
  );

  return (
    <div style={{ maxHeight: "600px", overflowY: "auto" }}>
      <DataTable
        columnContentTypes={columnContentTypes}
        headings={headings}
        rows={sortedRows}
        sortable={headings.map(() => true)}
        defaultSortDirection="descending"
        initialSortColumnIndex={1}
        onSort={handleSort}
      />
    </div>
  );
}

export default function ProductInsightsPage() {
  const { selectedShopId, setToast } = useAdmin();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchInsights = useCallback(() => {
    if (!selectedShopId) return;
    setLoading(true);
    fetcher(`/api/shops/${selectedShopId}/products/insights`)
      .then((res) => res.json())
      .then(setInsights)
      .catch((err) => {
        console.error(err);
        setToast("Error loading product insights.");
      })
      .finally(() => setLoading(false));
  }, [selectedShopId, setToast]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const handleSync = () => {
    if (!selectedShopId) return;
    setSyncing(true);
    fetcher(`/api/shops/${selectedShopId}/products/sync`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || "Sync failed");
        }
        setToast("Product sync started. Data will refresh when complete.");
        fetchInsights();
      })
      .catch((err) => {
        console.error(err);
        setToast(err.message || "Error starting product sync.");
      })
      .finally(() => setSyncing(false));
  };

  const canSync =
    insights?.last_sync_at != null
      ? (Date.now() / 1000 - insights.last_sync_at) >= 3600
      : true;

  const lastSyncLabel =
    insights?.last_sync_at != null
      ? `Last synced: ${new Date(insights.last_sync_at * 1000).toLocaleString()}`
      : "Never synced";

  const exportTagsCsv = () => {
    if (!insights?.by_tag?.length) return;
    const rows = insights.by_tag.map(({ tag, count }) => [tag, count]);
    exportToCsv(rows, ["Tag", "Product Count"], "products-by-tag.csv");
  };

  return (
    <Page
      title="Product Insights"
      primaryAction={{
        content: "Sync Products",
        onAction: handleSync,
        loading: syncing,
        disabled: !canSync || syncing,
      }}
    >
      {!canSync && insights?.last_sync_at != null && (
        <Banner
          title="Sync rate limited"
          tone="info"
          onDismiss={() => {}}
        >
          Product sync can only run once per hour. {lastSyncLabel}
        </Banner>
      )}

      {loading ? (
        <Card sectioned>
          <Spinner />
        </Card>
      ) : insights ? (
        <Layout>
          <Layout.Section>
            <Card title="Summary" sectioned>
              <DataTable
                columnContentTypes={["text", "numeric"]}
                headings={["Metric", "Count"]}
                rows={[
                  ["Total products", insights.total],
                  ["Products without images", insights.no_images_count],
                  ...insights.by_status.map(({ status, count }) => [
                    `Status: ${status}`,
                    count,
                  ]),
                ]}
              />
            </Card>
          </Layout.Section>

          <Layout.Section oneHalf>
            <Card
              title="Products by Tag"
              sectioned
              actions={[
                {
                  content: "Export CSV",
                  onAction: exportTagsCsv,
                  disabled: !insights.by_tag?.length,
                },
              ]}
            >
              {insights.by_tag?.length > 0 ? (
                <SortableTable
                  columnContentTypes={["text", "numeric"]}
                  headings={["Tag", "Product Count"]}
                  initialRows={insights.by_tag.map(({ tag, count }) => [tag, count])}
                />
              ) : (
                <p>No tags found.</p>
              )}
            </Card>
          </Layout.Section>

          {insights.by_product_type?.length > 0 && (
            <Layout.Section oneHalf>
              <Card title="Products by Type" sectioned>
                <SortableTable
                  columnContentTypes={["text", "numeric"]}
                  headings={["Product Type", "Product Count"]}
                  initialRows={insights.by_product_type
                    .slice(0, 15)
                    .map(({ product_type, count }) => [
                      product_type || "(blank)",
                      count,
                    ])}
                />
              </Card>
            </Layout.Section>
          )}

          <Layout.Section oneHalf>
            <Card title="Products by Vendor" sectioned>
              {insights.by_vendor?.length > 0 ? (
                <SortableTable
                  columnContentTypes={["text", "numeric"]}
                  headings={["Vendor", "Product Count"]}
                  initialRows={insights.by_vendor.slice(0, 50).map(({ vendor, count }) => [
                    vendor,
                    count,
                  ])}
                />
              ) : (
                <p>No vendors found.</p>
              )}
            </Card>
          </Layout.Section>
        </Layout>
      ) : (
        <Card sectioned>
          <p>Unable to load product insights.</p>
        </Card>
      )}
    </Page>
  );
}
